import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useClientMatches } from '../hooks/useMatches'
import {
  useCreateProposal,
  useMarkProposalSent,
  useProposalsList,
} from '../hooks/useProposals'
import { useCreateParticipation } from '../hooks/useParticipation'
import { useMe } from '../hooks/useAuth'
import { useClient } from '../hooks/useClient'
import { hasTokens } from '../lib/auth'
import { canWriteParticipation } from '../lib/permissions'
import { ProposalCard } from '../components/proposal/ProposalCard'
import { ProposalPreviewModal } from '../components/proposal/ProposalPreviewModal'
import type { MatchForClientRow, ProposalRow } from '../lib/api'
import { formatShortCode } from '../lib/shortCode'
import { effectivePrice } from '../lib/price'

type MatchSearch = {
  limit: number
  min_score: number
}

export const Route = createFileRoute('/clienti/$id/match')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): MatchSearch => {
    const toNum = (v: unknown, dflt: number, min: number, max: number): number => {
      const n = typeof v === 'number' ? v : Number(v)
      if (!Number.isFinite(n)) return dflt
      return Math.min(max, Math.max(min, n))
    }
    return {
      limit: toNum(search.limit, 20, 1, 100),
      min_score: toNum(search.min_score, 40, 0, 100),
    }
  },
  component: ClientMatchPage,
})

type TabId = 'da-segnalare' | 'segnalati' | 'chiusi' | 'esplora'

function ClientMatchPage() {
  const { id } = Route.useParams()
  const [tab, setTab] = useState<TabId>('da-segnalare')
  const [openProposalId, setOpenProposalId] = useState<string | null>(null)

  const { data: client } = useClient(id)

  const daSegnalareQ = useProposalsList({
    client_id: id,
    status: 'draft',
    auto_created: true,
    page_size: 100,
  })
  const segnalatiQ = useProposalsList({
    client_id: id,
    // 'segnalato' = lotti flaggati via bulk (da contattare), oltre agli inviati.
    status: 'segnalato,sent,read,visit_scheduled,visit_done',
    page_size: 100,
  })
  const chiusiQ = useProposalsList({
    client_id: id,
    status: 'won,lost,rejected',
    page_size: 100,
  })

  const openProposal = useMemo<ProposalRow | null>(() => {
    if (!openProposalId) return null
    const all: ProposalRow[] = [
      ...(daSegnalareQ.data?.items ?? []),
      ...(segnalatiQ.data?.items ?? []),
      ...(chiusiQ.data?.items ?? []),
    ]
    return all.find((p) => p.id === openProposalId) ?? null
  }, [openProposalId, daSegnalareQ.data, segnalatiQ.data, chiusiQ.data])

  return (
    <>
      <div className="page-header">
        <h1>Match per Cliente</h1>
        <span className="muted">Cliente ID: {id}</span>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-1)',
          borderBottom: '1px solid var(--line)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <TabButton
          active={tab === 'da-segnalare'}
          onClick={() => setTab('da-segnalare')}
          label="🔔 Da segnalare"
          count={daSegnalareQ.data?.total}
        />
        <TabButton
          active={tab === 'segnalati'}
          onClick={() => setTab('segnalati')}
          label="📤 Segnalati"
          count={segnalatiQ.data?.total}
        />
        <TabButton
          active={tab === 'chiusi'}
          onClick={() => setTab('chiusi')}
          label="✅ Chiusi"
          count={chiusiQ.data?.total}
        />
        <TabButton
          active={tab === 'esplora'}
          onClick={() => setTab('esplora')}
          label="🔍 Esplora"
        />
      </div>

      {tab === 'da-segnalare' && (
        <ProposalList
          query={daSegnalareQ}
          onOpen={(p) => setOpenProposalId(p.id)}
          emptyMsg="Nessun lotto da segnalare. Nuovi match arriveranno automaticamente."
        />
      )}
      {tab === 'segnalati' && (
        <ProposalList
          query={segnalatiQ}
          onOpen={(p) => setOpenProposalId(p.id)}
          emptyMsg="Nessuna proposta inviata."
        />
      )}
      {tab === 'chiusi' && (
        <ProposalList
          query={chiusiQ}
          onOpen={(p) => setOpenProposalId(p.id)}
          emptyMsg="Nessuna proposta chiusa."
        />
      )}
      {tab === 'esplora' && <ExploreWorkbench clientId={id} />}

      {openProposal && client && (
        <ProposalPreviewModal
          proposal={openProposal}
          clientEmail={client.email ?? null}
          clientPhone={client.telefono ?? null}
          onClose={() => setOpenProposalId(null)}
        />
      )}
    </>
  )
}

function TabButton({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean
  onClick: () => void
  label: string
  count?: number
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 0,
        padding: '8px 16px',
        borderBottom: active
          ? '2px solid var(--accent)'
          : '2px solid transparent',
        color: active ? 'var(--ink-1)' : 'var(--ink-3)',
        cursor: 'pointer',
        fontSize: 'var(--fs-13)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-1)',
      }}
    >
      {label}
      {count !== undefined && count > 0 && (
        <span
          className="muted"
          style={{
            fontSize: 'var(--fs-11)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          ({count})
        </span>
      )}
    </button>
  )
}

function ProposalList({
  query,
  onOpen,
  emptyMsg,
}: {
  query: ReturnType<typeof useProposalsList>
  onOpen: (p: ProposalRow) => void
  emptyMsg: string
}) {
  if (query.isPending) {
    return <div className="muted">Caricamento…</div>
  }
  if (query.isError) {
    return <div className="error">{query.error?.message}</div>
  }
  const items = query.data?.items ?? []
  if (items.length === 0) {
    return (
      <div className="muted" style={{ padding: 'var(--sp-4)' }}>
        {emptyMsg}
      </div>
    )
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      {items.map((p) => (
        <ProposalCard
          key={p.id}
          proposal={p}
          onOpen={() => onOpen(p)}
        />
      ))}
    </div>
  )
}

// ─── Formatters (used by ExploreWorkbench) ────────────────────────────────────

const currencyFmt = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const percentFmt = new Intl.NumberFormat('it-IT', {
  style: 'percent',
  maximumFractionDigits: 1,
})

function formatCurrency(v: number | null): string {
  if (v === null || v === undefined) return ''
  return currencyFmt.format(v)
}

function formatPercent(v: number | null): string {
  if (v === null || v === undefined) return ''
  return percentFmt.format(v)
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}/${mm}/${yyyy}`
}

type DataAstaStatus = 'future' | 'no_date' | 'past'
function dataAstaStatus(iso: string | null): DataAstaStatus {
  if (!iso) return 'no_date'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'no_date'
  return d.getTime() > Date.now() ? 'future' : 'past'
}

function DataAstaCell({ iso }: { iso: string | null }) {
  const status = dataAstaStatus(iso)
  if (status === 'no_date') {
    return (
      <span
        className="chip warn"
        title="Data asta non ancora definita dal tribunale. Verifica portale o custode."
        style={{
          fontSize: 'var(--fs-10)',
          padding: '2px 8px',
          background: 'oklch(96% 0.05 80)',
          color: 'oklch(45% 0.15 60)',
          border: '1px solid oklch(85% 0.08 80)',
          borderRadius: 'var(--r-pill)',
          fontWeight: 'var(--fw-semibold)',
          whiteSpace: 'nowrap',
        }}
      >
        ⚠ Data da definire
      </span>
    )
  }
  if (status === 'past') {
    return (
      <span
        className="muted"
        title="Asta gia svolta — filtro backend non applicato (cache?). Ricarica."
        style={{ fontSize: 'var(--fs-11)', color: 'var(--risk-ink, #b91c1c)' }}
      >
        ⊘ {formatDate(iso)}
      </span>
    )
  }
  return <span style={{ fontFamily: 'var(--font-mono)' }}>{formatDate(iso)}</span>
}

function scoreColor(score: number): string {
  if (score >= 70) return '#1a8a3a'
  if (score >= 50) return '#cc7a00'
  return '#777'
}

function breakdownLabel(b: MatchForClientRow['breakdown']): string {
  const parts: string[] = []
  if (b.budget_fit > 0) parts.push(`B${Math.round(b.budget_fit)}`)
  if (b.zone_match > 0) parts.push(`Z+${Math.round(b.zone_match)}`)
  if (b.semantic > 0) parts.push(`S+${Math.round(b.semantic)}`)
  if (b.rating_bonus > 0) parts.push(`R+${Math.round(b.rating_bonus)}`)
  return parts.length ? `${parts.join(' ')} = ${b.total}` : `= ${b.total}`
}

// ─── ExploreWorkbench ─────────────────────────────────────────────────────────
// Preserved workbench logic from original ClientMatchPage, now scoped to a
// dedicated inner component. clientId replaces Route.useParams().id.

function ExploreWorkbench({ clientId }: { clientId: string }) {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/clienti/$id/match' })
  const { data: me } = useMe()
  const canParticipate = canWriteParticipation(me)
  const [minScoreLocal, setMinScoreLocal] = useState<number>(search.min_score)
  const [pendingPropId, setPendingPropId] = useState<string | null>(null)
  const [errorPropId, setErrorPropId] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')

  // Keep local in sync with URL (e.g. on back nav).
  useEffect(() => {
    setMinScoreLocal(search.min_score)
  }, [search.min_score])

  const { data, isPending, isError, error } = useClientMatches(clientId, {
    limit: search.limit,
    min_score: search.min_score,
  })

  const createProposal = useCreateProposal()
  const markSent = useMarkProposalSent()
  const createParticipation = useCreateParticipation()
  const [partPendingPropId, setPartPendingPropId] = useState<string | null>(null)
  const [partErrorPropId, setPartErrorPropId] = useState<string | null>(null)
  const [partErrorMsg, setPartErrorMsg] = useState<string>('')

  function applyMinScore() {
    void navigate({
      search: ((s: MatchSearch) => ({
        ...s,
        min_score: minScoreLocal,
      })) as never,
    })
  }

  function onRowClick(m: MatchForClientRow) {
    if (m.property.source_url) {
      window.open(m.property.source_url, '_blank', 'noopener,noreferrer')
    }
  }

  async function onPartecipa(
    e: React.MouseEvent,
    m: MatchForClientRow,
  ): Promise<void> {
    e.stopPropagation()
    setPartErrorPropId(null)
    setPartErrorMsg('')
    setPartPendingPropId(m.property.id)
    try {
      const part = await createParticipation.mutateAsync({
        property_id: m.property.id,
        client_id: clientId,
      })
      void navigate({
        to: '/partecipazioni/$id',
        params: { id: part.id },
      } as never)
    } catch (err) {
      setPartErrorPropId(m.property.id)
      setPartErrorMsg(
        err instanceof Error ? err.message : 'Errore creazione partecipazione',
      )
    } finally {
      setPartPendingPropId(null)
    }
  }

  async function onSendWhatsApp(
    e: React.MouseEvent,
    m: MatchForClientRow,
  ): Promise<void> {
    e.stopPropagation()
    setErrorPropId(null)
    setErrorMsg('')
    setPendingPropId(m.property.id)
    try {
      const proposal = await createProposal.mutateAsync({
        property_id: m.property.id,
        client_id: clientId,
        channel: 'whatsapp',
      })
      if (!proposal.wa_url) {
        setErrorPropId(m.property.id)
        setErrorMsg('Telefono mancante')
        return
      }
      window.open(proposal.wa_url, '_blank', 'noopener,noreferrer')
      try {
        await markSent.mutateAsync(proposal.id)
      } catch {
        // Non-fatal: message was opened, sent flag is best-effort.
      }
    } catch (err) {
      setErrorPropId(m.property.id)
      setErrorMsg(err instanceof Error ? err.message : 'Errore invio proposta')
    } finally {
      setPendingPropId(null)
    }
  }

  return (
    <div>
      <div className="filters-row">
        <label className="muted" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Score minimo:
          <input
            type="number"
            min={0}
            max={100}
            value={minScoreLocal}
            onChange={(e) => setMinScoreLocal(Number(e.target.value) || 0)}
            className="filter-input"
            style={{ maxWidth: '6rem' }}
          />
          <button className="secondary" onClick={applyMinScore}>
            Applica
          </button>
        </label>
        {data && (
          <span className="muted">
            {data.total_matches} match su {data.total_candidates} candidati
          </span>
        )}
      </div>

      {isPending && <div className="muted">Caricamento…</div>}
      {isError && (
        <div className="error">
          {error?.message ?? 'Errore nel caricamento match'}
        </div>
      )}

      {data && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Score</th>
                <th>📱 WhatsApp</th>
                {canParticipate && <th>▶</th>}
                <th>Code</th>
                <th>Comune</th>
                <th>Indirizzo</th>
                <th>Tipologia</th>
                <th>Prezzo Base</th>
                <th>Margine</th>
                <th>Stage</th>
                <th>Data Asta</th>
                <th>Breakdown</th>
              </tr>
            </thead>
            <tbody>
              {data.items
                .filter((m) => dataAstaStatus(m.property.data_asta) === 'future')
                .map((m) => (
                <tr
                  key={m.property.id}
                  onClick={() => onRowClick(m)}
                  style={{ cursor: m.property.source_url ? 'pointer' : 'default' }}
                  title={m.property.source_url ?? undefined}
                >
                  <td>
                    <strong style={{ color: scoreColor(m.score) }}>
                      {m.score}
                    </strong>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="secondary"
                      disabled={pendingPropId === m.property.id}
                      onClick={(e) => void onSendWhatsApp(e, m)}
                      title={(m.property.address ?? '').slice(0, 80)}
                    >
                      {pendingPropId === m.property.id ? '…' : 'Invia'}
                    </button>
                    {errorPropId === m.property.id && (
                      <div
                        className="error"
                        style={{ fontSize: '0.75em', marginTop: '0.25rem' }}
                      >
                        {errorMsg}
                      </div>
                    )}
                  </td>
                  {canParticipate && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        className="secondary"
                        disabled={partPendingPropId === m.property.id}
                        onClick={(e) => void onPartecipa(e, m)}
                        title="Crea partecipazione"
                      >
                        {partPendingPropId === m.property.id
                          ? '…'
                          : '▶ Partecipa'}
                      </button>
                      {partErrorPropId === m.property.id && (
                        <div
                          className="error"
                          style={{ fontSize: '0.75em', marginTop: '0.25rem' }}
                        >
                          {partErrorMsg}
                        </div>
                      )}
                    </td>
                  )}
                  <td>
                    <span className="text-xs font-mono text-slate-500">
                      {formatShortCode('property', m.property.short_code)}
                    </span>
                  </td>
                  <td>{m.property.comune ?? ''}</td>
                  <td className="cell-truncate">{m.property.address ?? ''}</td>
                  <td>{m.property.tipologia ?? ''}</td>
                  <td className="cell-num">{formatCurrency(effectivePrice(m.property.offerta_minima, m.property.prezzo_base))}</td>
                  <td className="cell-num">{formatPercent(m.property.margine_stimato)}</td>
                  <td>{m.property.stage}</td>
                  <td>
                    <DataAstaCell iso={m.property.data_asta} />
                  </td>
                  <td>
                    <span className="muted" style={{ fontSize: '0.85em' }}>
                      {breakdownLabel(m.breakdown)}
                    </span>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr>
                  <td
                    colSpan={canParticipate ? 12 : 11}
                    className="muted"
                    style={{ textAlign: 'center' }}
                  >
                    Nessun match trovato (score ≥ {search.min_score})
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
